import React, { Component, ErrorInfo, ReactNode } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  AlertTitle,
  Stack,
  Divider,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Tooltip,
} from '@mui/material';
import { 
  Refresh as RefreshIcon, 
  BugReport as BugReportIcon,
  ExpandMore as ExpandMoreIcon,
  ContentCopy as ContentCopyIcon,
  Help as HelpIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { FinancialErrorHandler, FinancialError, FinancialErrorType } from '../../utils/financial-error-handling';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  onRetry?: () => void;
  showDetailedError?: boolean;
  enableErrorReporting?: boolean;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  errorId: string;
  financialError: FinancialError | null;
  retryCount: number;
  showDetails: boolean;
}

export class FinancialDashboardErrorBoundary extends Component<Props, State> {
  private retryTimeoutId: NodeJS.Timeout | null = null;

  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: '',
      financialError: null,
      retryCount: 0,
      showDetails: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Update state so the next render will show the fallback UI
    const errorId = `financial-error-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    // Try to parse as financial error
    let financialError: FinancialError | null = null;
    try {
      financialError = FinancialErrorHandler.parseApiError(error, {
        boundary: 'FinancialDashboardErrorBoundary',
        timestamp: new Date().toISOString()
      });
    } catch (parseError) {
      // Fallback to generic financial error
      financialError = {
        type: FinancialErrorType.UNKNOWN_FINANCIAL_ERROR,
        message: error.message || 'An unexpected error occurred in the financial dashboard',
        details: 'This error was caught by the error boundary',
        retryable: true,
        retryAfter: 30,
        suggestedAction: 'Try refreshing the dashboard or reloading the page',
        userMessage: 'Something went wrong with the financial dashboard. Please try again.',
        severity: 'high',
        context: { boundary: true, originalError: error.name }
      };
    }

    return {
      hasError: true,
      error,
      errorId,
      financialError,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Enhanced error logging with financial context
    const context = {
      boundary: 'FinancialDashboardErrorBoundary',
      componentStack: errorInfo.componentStack,
      errorBoundary: true,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      retryCount: this.state.retryCount
    };

    // Log using financial error handler
    if (this.state.financialError) {
      FinancialErrorHandler.logError(this.state.financialError, context);
    } else {
      console.error('Financial Dashboard Error Boundary:', error, context);
    }

    this.setState({
      error,
      errorInfo,
    });

    // Call optional error handler
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Report to monitoring service in production
    if (this.props.enableErrorReporting && process.env.NODE_ENV === 'production') {
      this.reportError(error, errorInfo, context);
    }
  }

  componentWillUnmount() {
    if (this.retryTimeoutId) {
      clearTimeout(this.retryTimeoutId);
    }
  }

  private reportError = async (error: Error, errorInfo: ErrorInfo, context: any) => {
    try {
      // In a real implementation, send to monitoring service
      // await monitoringService.captureException(error, {
      //   tags: { component: 'FinancialDashboard' },
      //   extra: { errorInfo, context }
      // });
      console.log('Error reported to monitoring service', { error, errorInfo, context });
    } catch (reportingError) {
      console.error('Failed to report error to monitoring service:', reportingError);
    }
  };

  handleRetry = () => {
    const newRetryCount = this.state.retryCount + 1;
    
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: '',
      financialError: null,
      retryCount: newRetryCount,
      showDetails: false,
    });

    // Call optional retry handler
    if (this.props.onRetry) {
      this.props.onRetry();
    }

    // Log retry attempt
    console.log(`Financial Dashboard retry attempt ${newRetryCount}`);
  };

  handleReload = () => {
    // Clear any cached data before reload
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => {
          if (name.includes('financial')) {
            caches.delete(name);
          }
        });
      });
    }
    
    window.location.reload();
  };

  handleCopyError = async () => {
    const errorDetails = {
      errorId: this.state.errorId,
      message: this.state.error?.message,
      stack: this.state.error?.stack,
      componentStack: this.state.errorInfo?.componentStack,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent
    };

    try {
      await navigator.clipboard.writeText(JSON.stringify(errorDetails, null, 2));
      // Could show a toast notification here
      console.log('Error details copied to clipboard');
    } catch (err) {
      console.error('Failed to copy error details:', err);
    }
  };

  toggleDetails = () => {
    this.setState(prev => ({ showDetails: !prev.showDetails }));
  };

  getSeverityIcon = (severity?: string) => {
    switch (severity) {
      case 'critical':
        return <ErrorIcon color="error" />;
      case 'high':
        return <ErrorIcon color="error" />;
      case 'medium':
        return <WarningIcon color="warning" />;
      case 'low':
        return <InfoIcon color="info" />;
      default:
        return <BugReportIcon color="error" />;
    }
  };

  getSeverityColor = (severity?: string): 'error' | 'warning' | 'info' => {
    switch (severity) {
      case 'critical':
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'info';
      default:
        return 'error';
    }
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const { financialError, error, errorInfo, errorId, retryCount, showDetails } = this.state;
      const displayInfo = financialError ? FinancialErrorHandler.formatForDisplay(financialError) : null;

      // Default enhanced error UI
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '400px',
            p: 3,
          }}
        >
          <Paper
            elevation={3}
            sx={{
              p: 4,
              maxWidth: 700,
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Stack spacing={3}>
              {/* Header with icon and title */}
              <Box>
                {this.getSeverityIcon(financialError?.severity)}
                <Typography variant="h4" gutterBottom sx={{ mt: 2 }}>
                  {displayInfo?.title || 'Financial Dashboard Error'}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {displayInfo?.message || financialError?.userMessage || 
                   'Something went wrong while loading the financial dashboard.'}
                </Typography>
              </Box>

              {/* Error severity and retry info */}
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
                {financialError?.severity && (
                  <Chip 
                    label={`${financialError.severity.toUpperCase()} SEVERITY`}
                    color={this.getSeverityColor(financialError.severity)}
                    size="small"
                  />
                )}
                {retryCount > 0 && (
                  <Chip 
                    label={`RETRY ATTEMPT ${retryCount}`}
                    color="info"
                    size="small"
                  />
                )}
                {financialError?.retryable && (
                  <Chip 
                    label="RETRYABLE"
                    color="success"
                    size="small"
                  />
                )}
              </Box>

              {/* Main error alert */}
              <Alert severity={displayInfo?.severity || 'error'}>
                <AlertTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  Error Details
                  <Tooltip title="Copy error details">
                    <IconButton size="small" onClick={this.handleCopyError}>
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </AlertTitle>
                <Typography variant="body2" component="div">
                  <strong>Error ID:</strong> {errorId}
                </Typography>
                {financialError?.type && (
                  <Typography variant="body2" component="div" sx={{ mt: 1 }}>
                    <strong>Type:</strong> {financialError.type}
                  </Typography>
                )}
                {error && (
                  <Typography variant="body2" component="div" sx={{ mt: 1 }}>
                    <strong>Message:</strong> {error.message}
                  </Typography>
                )}
              </Alert>

              {/* Suggested solutions */}
              {financialError && (
                <Box>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <HelpIcon color="primary" />
                    Suggested Solutions
                  </Typography>
                  <List dense>
                    {FinancialErrorHandler.getSuggestedSolutions(financialError).map((solution, index) => (
                      <ListItem key={index}>
                        <ListItemIcon>
                          <Typography variant="body2" color="primary">•</Typography>
                        </ListItemIcon>
                        <ListItemText 
                          primary={solution}
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              <Divider />

              {/* Action buttons */}
              <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap">
                <Button
                  variant="contained"
                  startIcon={<RefreshIcon />}
                  onClick={this.handleRetry}
                  size="large"
                  disabled={!!(financialError && !financialError.retryable)}
                >
                  {financialError?.retryAfter ? 
                    `Retry (wait ${Math.ceil(financialError.retryAfter / 60)}m)` : 
                    'Try Again'
                  }
                </Button>
                <Button
                  variant="outlined"
                  onClick={this.handleReload}
                  size="large"
                >
                  Reload Page
                </Button>
                {displayInfo?.actionUrl && (
                  <Button
                    variant="text"
                    startIcon={<HelpIcon />}
                    onClick={() => window.open(displayInfo.actionUrl, '_blank')}
                    size="large"
                  >
                    Get Help
                  </Button>
                )}
              </Stack>

              {/* Technical details accordion */}
              {(this.props.showDetailedError || process.env.NODE_ENV === 'development') && errorInfo && (
                <Accordion>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    onClick={this.toggleDetails}
                  >
                    <Typography variant="h6">
                      Technical Details {process.env.NODE_ENV === 'development' && '(Development)'}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Stack spacing={2}>
                      {/* Error stack */}
                      <Box>
                        <Typography variant="subtitle2" gutterBottom>
                          Error Stack:
                        </Typography>
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 2,
                            backgroundColor: 'grey.50',
                            textAlign: 'left',
                            overflow: 'auto',
                            maxHeight: 200,
                          }}
                        >
                          <Typography
                            variant="body2"
                            component="pre"
                            sx={{
                              fontFamily: 'monospace',
                              fontSize: '0.75rem',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                            }}
                          >
                            {error?.stack}
                          </Typography>
                        </Paper>
                      </Box>

                      {/* Component stack */}
                      <Box>
                        <Typography variant="subtitle2" gutterBottom>
                          Component Stack:
                        </Typography>
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 2,
                            backgroundColor: 'grey.50',
                            textAlign: 'left',
                            overflow: 'auto',
                            maxHeight: 200,
                          }}
                        >
                          <Typography
                            variant="body2"
                            component="pre"
                            sx={{
                              fontFamily: 'monospace',
                              fontSize: '0.75rem',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                            }}
                          >
                            {errorInfo.componentStack}
                          </Typography>
                        </Paper>
                      </Box>

                      {/* Financial error context */}
                      {financialError?.context && (
                        <Box>
                          <Typography variant="subtitle2" gutterBottom>
                            Error Context:
                          </Typography>
                          <Paper
                            variant="outlined"
                            sx={{
                              p: 2,
                              backgroundColor: 'grey.50',
                              textAlign: 'left',
                              overflow: 'auto',
                              maxHeight: 200,
                            }}
                          >
                            <Typography
                              variant="body2"
                              component="pre"
                              sx={{
                                fontFamily: 'monospace',
                                fontSize: '0.75rem',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                              }}
                            >
                              {JSON.stringify(financialError.context, null, 2)}
                            </Typography>
                          </Paper>
                        </Box>
                      )}
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              )}
            </Stack>
          </Paper>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default FinancialDashboardErrorBoundary;